import React from "react";
import PropTypes from "prop-types";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  Stack,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import {
  AccountBalanceOutlined as GovIcon,
  AccountBalanceWalletOutlined as WalletIcon,
  SaveOutlined as SaveIcon,
  CloudSyncOutlined as SyncIcon,
  InfoOutlined as InfoIcon,
  GavelOutlined as LegalIcon,
} from "@mui/icons-material";

import { colors, customShadows, borderRadiusPx } from "../../../../theme";
import { CommonButton, CommonFormField, CommonInput, CommonChip } from "../../ui";

/**
 * Government Tax Configuration Form (Collected by government, independent of platform)
 */
const TaxConfigForm = ({
  formData,
  onChange,
  onSave,
  onSyncContract,
  saving = false,
  syncing = false,
  currency = "POL",
}) => {
  const handleChange = (field) => (event) => {
    onChange({
      ...formData,
      [field]: event.target.value,
    });
  };

  const taxRate = Math.min(100, Math.max(0, parseFloat(formData.taxRate) || 0));
  const sampleAmount = 100;
  const sampleTaxDeducted = ((sampleAmount * taxRate) / 100).toFixed(2);
  const sampleWinnerPayout = (sampleAmount - sampleTaxDeducted).toFixed(2);

  return (
    <Card
      sx={{
        borderRadius: borderRadiusPx.card,
        border: `1px solid ${colors.dashboard.cardBorder}`,
        boxShadow: customShadows.cardSubtle,
        backgroundColor: colors.dashboard.cardBg,
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 3,
          pb: 2.5,
          borderBottom: `1px solid ${colors.dashboard.divider}`,
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          gap: 1.5,
        }}
      >
        <Box>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: "10px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: colors.primary.lighter,
                color: colors.primary.main,
              }}
            >
              <GovIcon sx={{ fontSize: 22 }} />
            </Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                fontSize: "1.15rem",
                color: colors.dashboard.title,
                letterSpacing: "-0.01em",
              }}
            >
              Set Government Tax
            </Typography>
          </Stack>
          <Typography
            variant="body2"
            sx={{
              color: colors.slate[500],
              fontSize: "0.85rem",
              mt: 0.75,
            }}
          >
            Configure statutory government tax rates on winning payouts. This tax is collected for the government and is completely independent from AutoBET platform revenue.
          </Typography>
        </Box>

        
      </Box>

      {/* Form Inputs */}
      <CardContent sx={{ p: { xs: 2.5, sm: 3.5 } }}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSave();
          }}
        >
          <Grid container spacing={3}>
            {/* 1. Government Tax Percentage */}
            <Grid item xs={12} sm={5}>
              <CommonFormField
                label="Government Tax Percentage"
                required
                helperText="Statutory rate collected on behalf of the government (0 - 100%)"
              >
                <CommonInput
                  type="number"
                  name="taxRate"
                  value={formData.taxRate}
                  onChange={handleChange("taxRate")}
                  placeholder="e.g. 10"
                  inputProps={{ min: 0, max: 100, step: "0.1" }}
                  startAdornment={
                    <InputAdornment position="start">
                      <GovIcon sx={{ fontSize: 18, color: colors.primary.main }} />
                    </InputAdornment>
                  }
                  endAdornment={<InputAdornment position="end">%</InputAdornment>}
                />
              </CommonFormField>
            </Grid>

            {/* 2. Wallet */}
            <Grid item xs={12} sm={7}>
              <CommonFormField
                label="Wallet"
                required
                // helperText="Statutory trust wallet where government tax funds are remitted"
              >
                <CommonInput
                  type="text"
                  name="taxWallet"
                  value={formData.taxWallet}
                  onChange={handleChange("taxWallet")}
                  placeholder="0x00000000000000000000000000000000000000000000"
                  startAdornment={
                    <InputAdornment position="start">
                      <WalletIcon sx={{ fontSize: 18, color: colors.slate[400] }} />
                    </InputAdornment>
                  }
                />
              </CommonFormField>
            </Grid>

            {/* Statutory Disclaimer & Live Calculation Preview Banner */}
            <Grid item xs={12}>
              <Box
                sx={{
                  p: 2.25,
                  bgcolor: colors.slate[50],
                  borderRadius: "12px",
                  border: `1px solid ${colors.slate[200]}`,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.25,
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <LegalIcon sx={{ fontSize: 18, color: colors.purple[700] }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: colors.slate[800], fontSize: "0.88rem" }}>
                    Statutory Government Notice:
                  </Typography>
                </Stack>
                <Typography variant="body2" sx={{ color: colors.slate[600], fontSize: "0.82rem", lineHeight: 1.5 }}>
                  Gaming Tax Liability is a statutory amount collected in trust for the government authority. This is <strong>independent from platform income</strong> and does not constitute AutoBET revenue.
                </Typography>

                <Box
                  sx={{
                    mt: 0.5,
                    pt: 1.25,
                    borderTop: `1px dashed ${colors.slate[300]}`,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <InfoIcon sx={{ fontSize: 16, color: colors.primary.main }} />
                  <Typography variant="body2" sx={{ color: colors.slate[700], fontSize: "0.82rem" }}>
                    <strong>Live Calculation:</strong> For a prize of <strong>100 {currency}</strong>, <strong>{taxRate}% ({sampleTaxDeducted} {currency})</strong> will be collected as government tax, and <strong>{sampleWinnerPayout} {currency}</strong> will be sent to the winner.
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* Action Buttons */}
            <Grid item xs={12}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent="flex-start" sx={{ mt: 1 }}>
                <CommonButton
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={saving || syncing}
                  startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                  sx={{ minWidth: 180, py: 1.25, fontWeight: 700 }}
                >
                  {saving ? "Saving..." : "Save"}
                </CommonButton>

                
              </Stack>
            </Grid>
          </Grid>
        </form>
      </CardContent>
    </Card>
  );
};

TaxConfigForm.propTypes = {
  formData: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onSyncContract: PropTypes.func,
  saving: PropTypes.bool,
  syncing: PropTypes.bool,
  currency: PropTypes.string,
};

export default TaxConfigForm;
